import { Dialog } from '@angular/cdk/dialog';
import { I18nPluralPipe, NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TippyDirective } from '@ngneat/helipopper';
import { map, Observable } from 'rxjs';

import { ServiceItemTooltipComponent } from '@app/modules/service-items/components';
import { DialogData } from '@app/modules/service-items/service-items.entity';
import { getServiceItemDialog } from '@app/modules/service-items/utils';
import { LoginDialog } from '@app/ui/dialogs';
import { MediaQueryService } from '@core/breakpoints';
import { PreloadRectComponent } from '@core/common/directives';
import { NumberPipe } from '@core/common/pipes';
import { days } from '@core/common/plurals';
import {
  addLeadingZero,
  Countdown,
  countdown,
  indicateLoading,
  isString
} from '@core/helpers';

import { ServiceItemImagePipe } from '@app/modules/service-items/pipes';
import { AuthState, User } from '@app/modules/users';
import { Avatar, Reward, RewardStatus } from '@app/modules/daily-rewards/daily-rewards.entity';
import { DailyRewardsState } from '@app/modules/daily-rewards/daily-rewards.state';
import { AvatarBonusDialog } from '@app/modules/daily-rewards/dialogs/avatar-bonus/avatar-bonus.dialog';

@Component({
  selector: 'bonus-item',
  templateUrl: './bonus-item.component.html',
  styleUrl: './bonus-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full flex flex-col mr-[0.4rem] last:mr-0' },
  imports: [
    NgClass,
    TippyDirective,
    NgStyle,
    NumberPipe,
    I18nPluralPipe,
    ServiceItemImagePipe,
    PreloadRectComponent
  ]
})
export class BonusItemComponent {
  private readonly authState = inject(AuthState);
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mediaService = inject(MediaQueryService);
  public readonly dailyRewardsState = inject(DailyRewardsState);
  public readonly isLg = toSignal(this.mediaService.mediaQuery('max', 'lg'));

  public baseButtonClass = 'h-[2.8rem] bg-[rgba(109,141,232,0.24)] w-full text-[1rem] leading-[1.5rem] rounded-[0.8rem] flex items-center justify-center lg:h-[2rem] lg:text-[0.8rem]';

  public isNextAndUnavailable!: boolean;

  @Input('reward')
  set _reward(reward: Reward | undefined) {
    this.reward = reward;
    this.isNextAndUnavailable = !!reward && reward.status === RewardStatus.UNAVAILABLE && !!reward.isNext;

    if (reward) {
      this.setupCountdown(reward.nextRewardAt);
    }
  }

  @Input()
  public isAuthenticated!: boolean;

  @Input()
  public isLastAndActive!: boolean;

  @Input()
  public isFirstElement!: boolean;

  @Input()
  public isLastElement!: boolean;

  @Input()
  public avatars!: Avatar[];

  @Input()
  public loggedUser!: User | null;

  public readonly isTakingReward = signal<boolean>(false);
  public readonly daysPlurals = days;
  public readonly ServiceItemTooltipComponent = ServiceItemTooltipComponent;
  public readonly RewardStatus = RewardStatus;

  public countdown$!: Observable<string>;
  public reward?: Reward;

  takeReward(): void {
    if (!this.reward) {
      return;
    }

    this.dailyRewardsState
      .takeReward(this.reward.id)
      .pipe(indicateLoading(this.isTakingReward), takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  openAvatarBonusDialog(): void {
    if (this.authState.isAuthenticated()) {
      if (!this.dailyRewardsState.conditionsMet()) {
        this.dialog.open(AvatarBonusDialog, {
          data: {
            modalDesc: $localize`:@@bonuses.daily_rewards.avatar_bonus.subtitle:Установи аватар и получи доступ эксклюзивным призам от CSFAIL`,
            avatars: this.avatars,
            loggedUser: this.loggedUser!
          }
        });
      }
    } else {
      this.dialog.open(LoginDialog);
    }
  }

  async useReward() {
    const { type } = this.reward!.serviceItem;

    const dialogComponent = getServiceItemDialog(type);

    if (!dialogComponent) {
      return;
    }

    const dialogData: DialogData = {
      serviceItem: this.reward!.serviceItem,
      inventoryItem: this.reward!.userReward?.inventoryServiceItem || undefined,
      callback: () => this.markRewardAsUsed()
    };

    const cmp = await dialogComponent();

    this.dialog.open(cmp.default, {
      data: dialogData
    });
  }

  private setupCountdown(nextRewardAt?: string | null): void {
    if (!isString(nextRewardAt)) {
      return;
    }

    this.countdown$ = countdown(nextRewardAt).pipe(
      map((cd: Countdown) => {
        const hours = addLeadingZero(cd.duration!.hours(), false);
        const minutes = addLeadingZero(cd.duration!.minutes(), false);
        const seconds = addLeadingZero(cd.duration!.seconds(), false);

        return `${hours}:${minutes}:${seconds}`;
      })
    );
  }

  private markRewardAsUsed(): void {
    const userRewardId = this.reward!.userReward!.id;

    this.dailyRewardsState.markRewardAsUsed(userRewardId);
  }
}
